/** @format */

import React, { useState } from "react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { CartItem } from "../types";
import {
  TrashIcon,
  ShoppingBagIcon,
  ArrowLeftIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import AIChatButton from '../components/AIChatButton';
import IOSCheckoutModal from '../components/IOSCheckoutModal';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    cartItems, 
    removeFromCart, 
    clearCart, 
    getCartTotal, 
    getCartSubtotal,
    loading 
  } = useCart();
  const { currentUser } = useAuth();
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Determine the best back navigation path
  const getBackNavigation = () => {
    // Check if there's a previous page in history
    if (location.state?.from) {
      return { path: location.state.from, label: "Go Back" };
    }

    // Analyze cart contents to suggest the most appropriate navigation
    const hasCourses = cartItems.some(item => item.type === "course");
    const hasBooks = cartItems.some(item => item.type === "book");
    const hasVideos = cartItems.some(item => item.type === "video");

    if (hasCourses && !hasBooks && !hasVideos) {
      return { path: "/app/catalog", label: "Back to Course Catalog" };
    } else if (hasBooks && !hasCourses && !hasVideos) {
      return { path: "/app/shop", label: "Back to Shop" };
    } else if (hasVideos && !hasCourses && !hasBooks) {
      return { path: "/app/catalog", label: "Back to Course Catalog" };
    } else if (hasCourses && hasBooks) {
      // Mixed cart - go to catalog as it's more comprehensive
      return { path: "/app/catalog", label: "Back to Course Catalog" };
    } else {
      // Default fallback
      return { path: "/app/catalog", label: "Back to Course Catalog" };
    }
  };

  const backNavigation = getBackNavigation();

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
  };

  const handleClearCart = () => {
    if (cartItems.length === 0) return;

    if (window.confirm("Are you sure you want to clear your cart?")) {
      clearCart();
    }
  };

  const handleCheckout = () => {
    if (!currentUser) {
      toast.error("Please sign in to checkout");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Check if iOS WebView and show modal instead of direct navigation
    if (shouldShowIOSWebViewAlert()) {
      setShowIOSModal(true);
      return;
    }

    // Navigate to checkout page for non-iOS WebView
    navigate('/app/checkout');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
    }).format(price);
  };

  const getItemImage = (item: any) => {
    if (item.image) return item.image;

    // Default images based on type
    switch (item.type) {
      case "course":
        return "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=150&h=150&fit=crop";
      case "book":
        return "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=150&h=150&fit=crop";
      case "video":
        return "https://images.unsplash.com/photo-1574717024653-61fd2cb4c2be?w=150&h=150&fit=crop";
      default:
        return "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=150&h=150&fit=crop";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cart...</p>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
                  <div className="mb-8">
          <Link
            to={backNavigation.path}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {backNavigation.label}
          </Link>
            <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
          </div>

          {/* Empty Cart */}
          <div className="text-center py-16">
            <ShoppingBagIcon className="mx-auto h-24 w-24 text-gray-300 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Your cart is empty
            </h2>
            <p className="text-gray-600 mb-8">
              Looks like you haven't added any items to your cart yet.
            </p>
            <div className="space-x-4">
              <Link
                to={backNavigation.path}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={backNavigation.path}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {backNavigation.label}
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-3xl font-bold text-gray-900 mb-4 sm:mb-0">
              Shopping Cart ({cartItems.length} items)
            </h1>
            <button
              onClick={handleClearCart}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Clear Cart
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Cart Items
                  </h2>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {cartItems.map((item: CartItem) => (
                  <div key={item.id} className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      {/* Item Image */}
                      <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4">
                        <img
                          src={getItemImage(item)}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0 mb-4 sm:mb-0 sm:mr-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="text-lg font-medium text-gray-900 truncate">
                                {item.name}
                              </h3>
                            </div>
                            <p className="text-sm text-gray-500 capitalize">
                              {item.type}
                            </p>
                            <p className="text-sm text-gray-400">
                              Added {item.addedAt.toLocaleDateString()}
                            </p>
                          </div>

                          <div className="flex items-center space-x-4 mt-2 sm:mt-0">
                            <div className="text-right">
                              <p className="text-lg font-semibold text-gray-900">
                                {formatPrice(item.price)}
                              </p>
                            </div>

                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
                              title="Remove item"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Order Summary
                </h2>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal ({cartItems.length} items)</span>
                  <span>{formatPrice(getCartSubtotal())}</span>
                </div>


                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tax</span>
                  <span>Calculated at checkout</span>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-semibold text-gray-900">
                    <span>Total</span>
                    <span>{formatPrice(getCartTotal())}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={!currentUser}
                className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <CreditCardIcon className="h-5 w-5 mr-2" />
                {currentUser ? (
                  shouldShowIOSWebViewAlert() ? "Checkout Online" : "Proceed to Checkout"
                ) : (
                  "Sign in to Checkout"
                )}
              </button>

              {!currentUser && !shouldShowIOSWebViewAlert() && (
                <p className="text-sm text-gray-500 text-center mt-3">
                  Please sign in to complete your purchase
                </p>
              )}

              <div className="mt-6 text-center">
                <Link
                  to={backNavigation.path}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  {shouldShowIOSWebViewAlert() ? "Continue Browsing" : "Continue Shopping"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* AI Chat Button */}
      <AIChatButton />

      {/* iOS Checkout Modal */}
      <IOSCheckoutModal
        isOpen={showIOSModal}
        onClose={() => setShowIOSModal(false)}
        cartItems={cartItems}
        cartTotal={getCartTotal()}
        customerInfo={currentUser}
      />
    </div>
  );
};

export default Cart;
